// Types
import {Dimensions, StyleSheet} from "react-native";

export interface Task {
  id: string;
  title: string;
  description?: string;
  priority?: "low" | "medium" | "high" | "critical";
  assignee?: string;
  tags?: string[];
  orderNumber?: string;
  dueDate?: string;
}

export interface Column {
  id: string;
  title: string;
  color: string;
  tasks: Task[];
}

// Helpers
export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "critical":
      return "#DC2626";
    case "high":
      return "#F59E0B";
    case "medium":
      return "#3B82F6";
    case "low":
      return "#10B981";
    default:
      return "#6B7280";
  }
};

export const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case "critical":
      return "alert-octagon";
    case "high":
      return "arrow-up-circle";
    case "medium":
      return "minus-circle";
    case "low":
      return "arrow-down-circle";
    default:
      return "circle-outline";
  }
};

export const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

export const staticData: Column[] = [
  {
    id: "incoming",
    title: "Incoming Orders",
    color: "#3B82F6",
    tasks: [
      {
        id: "1",
        title: "Order #12345",
        description: "Customer: ABC Corp - 500 units",
        priority: "high",
        orderNumber: "#12345",
        assignee: "Poojan P.",
        tags: ["Urgent", "China"],
        dueDate: "2026-02-05",
      },
      {
        id: "2",
        title: "Order #12346",
        description: "Customer: XYZ Ltd - 300 units",
        priority: "medium",
        orderNumber: "#12346",
        assignee: "Sarah M.",
        tags: ["Standard"],
        dueDate: "2026-02-10",
      },
    ],
  },
  {
    id: "in-production",
    title: "In Production",
    color: "#F59E0B",
    tasks: [
      {
        id: "3",
        title: "Order #12340",
        description: "Customer: DEF Inc - 750 units",
        priority: "medium",
        orderNumber: "#12340",
        assignee: "Mike R.",
        tags: ["Line 3"],
        dueDate: "2026-02-08",
      },
    ],
  },
  {
    id: "quality-check",
    title: "Quality Check",
    color: "#8B5CF6",
    tasks: [
      {
        id: "4",
        title: "Order #12335",
        description: "Customer: GHI Group - 200 units",
        priority: "low",
        orderNumber: "#12335",
        assignee: "Emma L.",
        tags: ["QC"],
        dueDate: "2026-02-06",
      },
    ],
  },
  {
    id: "ready-to-ship",
    title: "Ready to Ship",
    color: "#10B981",
    tasks: [
      {
        id: "5",
        title: "Order #12330",
        description: "Customer: JKL Partners - 1000 units",
        priority: "critical",
        orderNumber: "#12330",
        assignee: "David K.",
        tags: ["Express"],
        dueDate: "2026-02-04",
      },
      {
        id: "6",
        title: "Order #12329",
        description: "Customer: MNO Corp - 450 units",
        priority: "medium",
        orderNumber: "#12329",
        assignee: "Lisa P.",
        tags: ["Standard"],
        dueDate: "2026-02-07",
      },
    ],
  },
  {
    id: "completed",
    title: "Completed",
    color: "#6B7280",
    tasks: [
      {
        id: "7",
        title: "Order #12320",
        description: "Customer: PQR Ltd - 600 units",
        priority: "low",
        orderNumber: "#12320",
        assignee: "Tom W.",
        tags: ["Delivered"],
        dueDate: "2026-02-01",
      },
    ],
  },
]

const { height: SCREEN_HEIGHT, width: SCREEN_WIDTH } = Dimensions.get("window");
export const COLUMN_WIDTH = 300;

// Styles
export const kanbanStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F9FAFB" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerTitle: { fontSize: 24, fontWeight: "700", color: "#111827" },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3B82F6",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
  },
  addButtonText: { color: "#fff", fontWeight: "600" },
  board: { flex: 1, padding: 16 },
  column: {
    width: COLUMN_WIDTH,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginRight: 16,
    maxHeight: SCREEN_HEIGHT - 150,
  },
  columnHighlighted: {
    borderWidth: 2,
    borderColor: "#3B82F6",
    borderStyle: "dashed",
  },
  columnHeader: {
    padding: 12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnTitle: { color: "#fff", fontWeight: "700", fontSize: 14, flex: 1 },
  columnBadge: {
    backgroundColor: "rgba(255,255,255,0.3)",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  columnBadgeText: { color: "#fff", fontWeight: "700", fontSize: 12 },
  columnContent: { padding: 8, flex: 1 },
  addTaskButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  addTaskButtonText: { color: "#3B82F6", fontWeight: "600" },
  taskCard: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  taskCardDragging: { shadowOpacity: 0.3, shadowRadius: 8 },
  taskHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  taskTitle: { fontWeight: "600", flex: 1, fontSize: 14 },
  taskDescription: { color: "#6B7280", marginBottom: 8, fontSize: 13 },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 8,
  },
  tag: {
    backgroundColor: "#DBEAFE",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  tagText: { fontSize: 11, fontWeight: "600", color: "#1E40AF" },
  taskFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  assigneeContainer: { flexDirection: "row", gap: 6, alignItems: "center" },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#3B82F6",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { color: "#fff", fontSize: 10, fontWeight: "700" },
  assigneeText: { fontSize: 12, color: "#374151" },
  dueDateContainer: { flexDirection: "row", gap: 4, alignItems: "center" },
  dueDateText: { fontSize: 11, color: "#6B7280" },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: 24,
    borderRadius: 12,
    width: SCREEN_WIDTH * 0.9,
    maxWidth: 500,
    maxHeight: SCREEN_HEIGHT * 0.8,
  },
  modalTitle: { fontSize: 20, fontWeight: "700", marginBottom: 20 },
  label: { fontSize: 14, fontWeight: "600", marginBottom: 8, color: "#374151" },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 14,
  },
  textArea: { minHeight: 80, textAlignVertical: "top" },
  priorityContainer: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  priorityButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 2,
  },
  priorityButtonActive: {},
  priorityButtonText: { fontWeight: "600", fontSize: 13 },
  priorityButtonTextActive: { color: "#fff" },
  tagInputContainer: { flexDirection: "row", gap: 8, marginBottom: 8 },
  addTagButton: {
    backgroundColor: "#3B82F6",
    padding: 12,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  tagWithDelete: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#DBEAFE",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },
  colorContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  colorButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  colorButtonActive: {
    borderWidth: 3,
    borderColor: "#fff",
    shadowColor: "#000",
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  modalActions: { flexDirection: "row", gap: 12, marginTop: 16 },
  button: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    gap: 8,
  },
  buttonPrimary: { backgroundColor: "#3B82F6" },
  buttonSecondary: { backgroundColor: "#E5E7EB" },
  buttonPrimaryText: { color: "#fff", fontWeight: "600", fontSize: 15 },
  buttonSecondaryText: { color: "#374151", fontWeight: "600", fontSize: 15 },

  // Task Detail Modal Styles (Jira-like)
  detailModalContent: {
    backgroundColor: "#fff",
    borderRadius: 16,
    width: SCREEN_WIDTH * 0.95,
    maxWidth: 700,
    maxHeight: SCREEN_HEIGHT * 0.9,
    shadowColor: "#000",
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
  },
  detailHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  detailOrderNumber: {
    fontSize: 14,
    fontWeight: "600",
    color: "#6B7280",
  },
  detailColumnBadge: {
    fontSize: 12,
    color: "#9CA3AF",
    fontWeight: "500",
  },
  detailBody: {
    flex: 1,
    padding: 20,
  },
  detailTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 24,
    lineHeight: 32,
  },
  detailGrid: {
    gap: 20,
    marginBottom: 24,
  },
  detailField: {
    gap: 8,
  },
  detailFieldLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  detailPriorityBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  detailPriorityText: {
    fontSize: 14,
    fontWeight: "600",
  },
  detailAssigneeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  detailAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3B82F6",
    alignItems: "center",
    justifyContent: "center",
  },
  detailAvatarText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  detailAssigneeName: {
    fontSize: 15,
    fontWeight: "500",
    color: "#111827",
  },
  detailDateContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  detailDateText: {
    fontSize: 14,
    color: "#374151",
  },
  detailSection: {
    marginBottom: 24,
  },
  detailSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 12,
  },
  detailDescriptionBox: {
    backgroundColor: "#F9FAFB",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  detailDescription: {
    fontSize: 14,
    lineHeight: 22,
    color: "#374151",
  },
  detailTagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  detailTag: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#EFF6FF",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DBEAFE",
  },
  detailTagText: {
    fontSize: 13,
    fontWeight: "500",
    color: "#1E40AF",
  },
  activityItem: {
    flexDirection: "row",
    gap: 12,
    backgroundColor: "#F9FAFB",
    padding: 12,
    borderRadius: 8,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#D1FAE5",
    alignItems: "center",
    justifyContent: "center",
  },
  activityText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
    marginBottom: 2,
  },
  activityTime: {
    fontSize: 12,
    color: "#6B7280",
  },
  detailFooter: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
});
